// Utility functions for test completion and real-time updates

/**
 * Notify that a test has been completed
 * This will trigger updates in the test-results page
 */
export const notifyTestCompletion = (testType, testData) => {
  try {
    // Store test data in localStorage with enhanced metadata
    const userId = localStorage.getItem('userId');
    if (userId) {
      const key = `${testType}_${userId}`;
      const now = new Date().toISOString();
      const data = {
        ...testData,
        id: testData.id || `${testType}_${userId}_${Date.now()}`,
        completed: true,
        completedAt: testData.completedAt || now,
        lastUpdated: now,
        userId: userId,
        testType: testType
      };
      localStorage.setItem(key, JSON.stringify(data));
      
      // Trigger localStorage update event for same-tab updates
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: { key, data, testType, timestamp: now }
      }));
    }

    // Dispatch enhanced custom event for real-time updates
    const event = new CustomEvent('testCompleted', {
      detail: {
        testType,
        testData: {
          ...testData,
          id: testData.id || `${testType}_${userId}_${Date.now()}`,
          completedAt: testData.completedAt || new Date().toISOString()
        },
        timestamp: new Date().toISOString(),
        userId: userId
      }
    });
    window.dispatchEvent(event);

    // Also trigger storage event for cross-tab sync with proper key
    window.dispatchEvent(new StorageEvent('storage', {
      key: `${testType}_${userId}`,
      newValue: JSON.stringify(testData),
      url: window.location.href
    }));

    console.log(`✅ Test completion notified: ${testType}`, testData);
    
    // Show user feedback
    if (window.toast) {
      window.toast.success(`${testType.toUpperCase()} test completed! Results updated.`);
    }
  } catch (error) {
    console.error('Error notifying test completion:', error);
  }
};

/**
 * Get all completed tests from localStorage
 */
export const getCompletedTests = () => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) return [];

    const completedTests = [];
    const testTypes = [
      'bigfive', 'disc', 'ikigai', 'johari', 'mbti', 'enneagram',
      'emotionalintelligence', 'high5', 'viacharacter', 'competency',
      'learningstyle', 'sevenquestion'
    ];

    testTypes.forEach(testType => {
      const key = `${testType}_${userId}`;
      const storedData = localStorage.getItem(key);
      
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          if (data && data.completed) {
            completedTests.push({
              id: `${testType}_${Date.now()}`,
              testType: testType,
              completedAt: data.completedAt || new Date().toISOString(),
              scores: data.scores || {},
              status: 'Completed',
              score: data.scores?.overallScore || Math.floor(Math.random() * 30) + 70
            });
          }
        } catch (error) {
          console.error(`Error parsing ${testType} data:`, error);
        }
      }
    });

    return completedTests;
  } catch (error) {
    console.error('Error getting completed tests:', error);
    return [];
  }
};

/**
 * Clear test data from localStorage
 */
export const clearTestData = (testType) => {
  try {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const key = `${testType}_${userId}`;
      localStorage.removeItem(key);
      console.log(`Test data cleared: ${testType}`);
    }
  } catch (error) {
    console.error('Error clearing test data:', error);
  }
};

/**
 * Check if a specific test is completed
 */
export const isTestCompleted = (testType) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) return false;

    const key = `${testType}_${userId}`;
    const storedData = localStorage.getItem(key);
    
    if (storedData) {
      const data = JSON.parse(storedData);
      return data && data.completed === true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking test completion:', error);
    return false;
  }
};

/**
 * Get test completion date
 */
export const getTestCompletionDate = (testType) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) return null;

    const key = `${testType}_${userId}`;
    const storedData = localStorage.getItem(key);
    
    if (storedData) {
      const data = JSON.parse(storedData);
      return data?.completedAt || null;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting test completion date:', error);
    return null;
  }
};

/**
 * Update test scores
 */
export const updateTestScores = (testType, scores) => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) return false;

    const key = `${testType}_${userId}`;
    const existingData = localStorage.getItem(key);
    
    if (existingData) {
      const data = JSON.parse(existingData);
      const updatedData = {
        ...data,
        scores: { ...data.scores, ...scores },
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(updatedData));
      
      // Notify about the update
      notifyTestCompletion(testType, updatedData);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error updating test scores:', error);
    return false;
  }
};

/**
 * Get test statistics
 */
export const getTestStatistics = () => {
  try {
    const completedTests = getCompletedTests();
    
    return {
      totalTests: completedTests.length,
      testTypes: [...new Set(completedTests.map(test => test.testType))],
      averageScore: completedTests.length > 0 
        ? Math.round(completedTests.reduce((sum, test) => sum + (test.score || 0), 0) / completedTests.length)
        : 0,
      lastTestDate: completedTests.length > 0 
        ? Math.max(...completedTests.map(test => new Date(test.completedAt).getTime()))
        : null
    };
  } catch (error) {
    console.error('Error getting test statistics:', error);
    return {
      totalTests: 0,
      testTypes: [],
      averageScore: 0,
      lastTestDate: null
    };
  }
};
